import { CSSProperties, FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { uniqBy } from 'lodash';
import {
  DragDropContext,
  DragStart,
  Draggable,
  DraggingStyle,
  DropResult,
  Droppable,
  NotDraggingStyle,
} from 'react-beautiful-dnd';
import AddIcon from '@material-ui/icons/Add';
import { ReactComponent as TriangleWarningIcon } from '../../../assets/svg/triangleWarningIcon.svg';
import { ReactComponent as InfoIcon } from '../../../assets/svg/InfoIconLight.svg';
import { ReactComponent as CogIcon } from '../../../assets/svg/cog.svg';
import TooltipContainer from '../../../components/shared/TooltipContainer/TooltipContainer';
import { DraggableIndicatorIcon } from '../../../components/svgs';
import BtButton from '../../bt-button/bt-button';
import { formatAmount } from '../../../util';
import { NewPriceFieldRow, OrderedRow } from '../../../models/priceRows.model';
import { PriceRow, PriceTable, bidRowValues, valueKey } from '../../../util/bidTotal';
import { movePriceField, visibleRows } from '../../../util/priceFieldRows';
import { RowValue } from '../../../util/priceRowValues';
import { CustomRowValueCell } from '../customRowValueCell/customRowValueCell';
import { CellValue } from '../customRowValueCell/cellInput';
import { PriceFieldRowModal } from '../priceFieldRowModal/priceFieldRowModal';
import styles from '../bid-overview.module.scss';

type PriceColumn = {
  key: string;
  tdProps: { className?: string; onMouseEnter: () => void; onMouseLeave: () => void };
  supplierId: string;
  refVersion: number;
  prices: PriceRow[];
  hasBid: boolean;
};

type Props = {
  columns: PriceColumn[];
  priceTable: PriceTable;
  isOrderLoaded: boolean;
  canEdit: boolean;
  onReorder: (order: string[]) => void;
  onSaveRow: (row: NewPriceFieldRow, editedRowId?: string) => Promise<boolean>;
  onDeleteRow: (customRowId: string) => Promise<boolean>;
  onSaveValue: (
    customRowId: string,
    supplierId: string,
    refVersion: number,
    value: CellValue,
  ) => void;
  onDragChange: (isDragging: boolean) => void;
};

const pricesAsRows = (prices: PriceRow[]): OrderedRow[] =>
  uniqBy(prices, 'id').map(({ id, name }) => ({ kind: 'price', id, name }));

export const PriceRowsBody: FC<Props> = ({
  columns,
  priceTable,
  isOrderLoaded,
  canEdit,
  onReorder,
  onSaveRow,
  onDeleteRow,
  onSaveValue,
  onDragChange,
}) => {
  const [t] = useTranslation('projects', { keyPrefix: 'singlePurchase.bidOverview' });
  const [rowModal, setRowModal] = useState<{ row?: OrderedRow } | null>(null);
  const cellWidthsRef = useRef<string[]>([]);
  const rowHeightRef = useRef<string | undefined>(undefined);

  const canDrag = canEdit && isOrderLoaded;
  const prices = columns.flatMap(column => column.prices);
  const pricedIds = new Set(prices.map(price => price.id));
  const renderedRows = isOrderLoaded
    ? visibleRows(priceTable.rows, pricedIds).map(row =>
        row.kind === 'sum' ? { ...row, name: t('totalSum', 'Totalpris') } : row,
      )
    : pricesAsRows(prices);
  const rowValues = columns.map(column =>
    column.hasBid
      ? bidRowValues(priceTable, column.prices, column.supplierId, column.refVersion)
      : undefined,
  );

  const handleBeforeDragStart = (start: DragStart) => {
    const row = document.querySelector<HTMLTableRowElement>(
      `[data-rbd-draggable-id="${CSS.escape(start.draggableId)}"]`,
    );
    if (!row) return;
    cellWidthsRef.current = Array.from(row.cells).map(c => `${c.getBoundingClientRect().width}px`);
    rowHeightRef.current = `${row.getBoundingClientRect().height}px`;
  };

  const handleDragEnd = (result: DropResult) => {
    onDragChange(false);
    const destination = result.destination;
    if (!destination || destination.index === result.source.index) return;

    onReorder(
      movePriceField(
        priceTable.rows.map(row => row.id),
        renderedRows.map(row => row.id),
        result.source.index,
        destination.index,
      ),
    );
  };

  const saveModalRow = async (row: NewPriceFieldRow) => {
    if (await onSaveRow(row, rowModal?.row?.id)) setRowModal(null);
  };

  const deleteModalRow = async () => {
    if (rowModal?.row && (await onDeleteRow(rowModal.row.id))) setRowModal(null);
  };

  const getRowStyle = (
    style: DraggingStyle | NotDraggingStyle | undefined,
    dragging: boolean,
  ): CSSProperties => {
    const next: CSSProperties = { ...style };
    if (dragging) {
      if (typeof next.transform === 'string' && next.transform.includes(',')) {
        next.transform = `translate(0px${next.transform.slice(next.transform.indexOf(','))}`;
      }
      next.height = rowHeightRef.current;
      next.display = 'table';
      next.background = 'white';
    }
    return next;
  };

  const rowWarnings = (entries: RowValue[]) => {
    const warnings: string[] = [];
    if (entries.some(entry => entry.problem === 'circular')) {
      warnings.push(
        t('customRow.problem.circular', 'Formeln hänvisar till sig själv via en annan rad'),
      );
    }
    if (entries.some(entry => entry.problem === 'invalid')) {
      warnings.push(t('customRow.problem.invalid', 'Formeln är inte giltig'));
    }
    return warnings;
  };

  const cellWarning = (problem: RowValue['problem']) => {
    if (problem === 'divisionByZero') {
      return t(
        'customRow.problem.divisionByZero',
        'Kan inte räknas ut eftersom formeln delar med noll',
      );
    }
    if (problem === 'brokenInput') {
      return t(
        'customRow.problem.brokenInput',
        'Kan inte räknas ut eftersom en rad som formeln använder inte kan räknas ut',
      );
    }
    return undefined;
  };

  const renderWarning = (warnings: string[]) => (
    <TooltipContainer
      renderReferenceComponent={(className, ref) => (
        <TriangleWarningIcon className={cx(className, styles.formulaWarningIcon)} ref={ref} />
      )}
    >
      <div style={{ maxWidth: '250px' }}>
        {warnings.map(warning => (
          <div key={warning}>{warning}</div>
        ))}
      </div>
    </TooltipContainer>
  );

  const renderEnteredValue = (row: OrderedRow, column: PriceColumn) => {
    const isText = row.kind === 'text';
    const entered =
      priceTable.enteredValues[valueKey(row.id, column.supplierId, column.refVersion)];
    const storedValue = isText
      ? entered?.text ?? ''
      : entered?.value == null
      ? ''
      : formatAmount(entered.value) || '0';
    return (
      <CustomRowValueCell
        key={storedValue}
        value={storedValue}
        isText={isText}
        unit={row.kind === 'value' ? row.unit : undefined}
        readOnly={!canEdit}
        onSave={value => onSaveValue(row.id, column.supplierId, column.refVersion, value)}
      />
    );
  };

  const renderPriceCell = (row: OrderedRow, column: PriceColumn, value: RowValue | undefined) => {
    switch (row.kind) {
      case 'sum':
        if (value?.amount === undefined) return '';
        return (
          <span>
            {formatAmount(value.amount) || '-'}&nbsp;{value.unit}
          </span>
        );
      case 'price': {
        const price = column.prices.find(p => p.id === row.id);
        return price?.price ? (
          <span>
            {price.price}&nbsp;{price.unit}
          </span>
        ) : (
          ''
        );
      }
      case 'formula': {
        const warning = cellWarning(value?.problem);
        if (warning) return renderWarning([warning]);
        if (!column.hasBid) return '';
        if (value?.amount === undefined) return '-';
        return (
          <span>
            {formatAmount(value.amount) || '0'}&nbsp;{row.unit}
          </span>
        );
      }
      case 'value':
      case 'text':
        return column.hasBid ? renderEnteredValue(row, column) : '';
    }
  };

  return (
    <>
      <DragDropContext
        onBeforeDragStart={handleBeforeDragStart}
        onDragStart={() => onDragChange(true)}
        onDragEnd={handleDragEnd}
      >
        <Droppable droppableId="priceFields">
          {(dropProvided, dropSnapshot) => (
            <tbody
              ref={dropProvided.innerRef}
              {...dropProvided.droppableProps}
              className={styles.priceFieldsBody}
            >
              {renderedRows.map((row, index) => (
                <Draggable
                  key={row.id}
                  draggableId={row.id}
                  index={index}
                  isDragDisabled={!canDrag}
                >
                  {(dragProvided, dragSnapshot) => {
                    const dragging = dragSnapshot.isDragging;
                    const entries = rowValues.map(values => values?.get(row.id));
                    const warnings =
                      row.kind === 'formula'
                        ? rowWarnings(entries.filter((entry): entry is RowValue => !!entry))
                        : [];
                    return (
                      <tr
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        style={getRowStyle(dragProvided.draggableProps.style, dragging)}
                      >
                        <td style={dragging ? { width: cellWidthsRef.current[0] } : undefined}>
                          <div className={styles.priceLabelCell}>
                            {canDrag && (
                              <span className={styles.dragHandle} {...dragProvided.dragHandleProps}>
                                <DraggableIndicatorIcon />
                              </span>
                            )}
                            <span>{row.name}</span>
                            {row.kind === 'sum' && (
                              <TooltipContainer
                                renderReferenceComponent={(className, ref) => (
                                  <InfoIcon className={className} ref={ref} />
                                )}
                              >
                                <div style={{ maxWidth: '250px' }}>
                                  {t(
                                    'totalSumTooltip',
                                    'Summerar alla rader ovanför Totalpris som har samma enhet som den första raden med en enhet.',
                                  )}
                                </div>
                              </TooltipContainer>
                            )}
                            {warnings.length > 0 && renderWarning(warnings)}
                            {row.kind !== 'price' && row.kind !== 'sum' && canEdit && (
                              <BtButton
                                class={styles.rowSettingsButton}
                                color="white"
                                size="xxs"
                                onClick={() => setRowModal({ row })}
                              >
                                <CogIcon />
                              </BtButton>
                            )}
                          </div>
                        </td>
                        {columns.map((column, i) => (
                          <td
                            key={column.key}
                            {...column.tdProps}
                            className={cx(column.tdProps.className, styles.right)}
                            style={dragging ? { width: cellWidthsRef.current[i + 1] } : undefined}
                          >
                            {renderPriceCell(row, column, entries[i])}
                          </td>
                        ))}
                      </tr>
                    );
                  }}
                </Draggable>
              ))}
              <tr
                aria-hidden
                style={{ height: dropSnapshot.isUsingPlaceholder ? rowHeightRef.current : 0 }}
              >
                <td colSpan={columns.length + 1} style={{ padding: 0, border: 'none' }}>
                  {dropProvided.placeholder}
                </td>
              </tr>
            </tbody>
          )}
        </Droppable>
      </DragDropContext>
      {canEdit && (
        <tbody>
          <tr className={styles.addRow}>
            <td>
              <BtButton
                class={styles.addRowButton}
                color="white"
                size="xxs"
                leftIcon={<AddIcon />}
                style={{ width: 'fit-content' }}
                onClick={() => setRowModal({})}
              >
                {t('buttons.addRow', 'Lägg till rad')}
              </BtButton>
            </td>
            {columns.map(column => (
              <td key={column.key} {...column.tdProps}>
                &nbsp;
              </td>
            ))}
          </tr>
        </tbody>
      )}
      {rowModal && (
        <PriceFieldRowModal
          row={rowModal.row}
          otherRows={renderedRows.filter(row => row.id !== rowModal.row?.id)}
          onClose={() => setRowModal(null)}
          onSave={saveModalRow}
          onDelete={deleteModalRow}
        />
      )}
    </>
  );
};
